import { Check } from 'lucide-react';

interface Zone {
    top: string;
    left: string;
    width: string;
    height: string;
    label: string;
}

type ChocType = 'voiture' | 'quad' | 'moto';

const ZONES: Record<ChocType, Record<string, Zone>> = {
    voiture: {
        top_left:     { top: '0%',  left: '0%',  width: '50%', height: '33%', label: 'Avant gauche' },
        top_right:    { top: '0%',  left: '50%', width: '50%', height: '33%', label: 'Avant droit' },
        left:         { top: '33%', left: '0%',  width: '50%', height: '34%', label: 'Côté gauche' },
        right:        { top: '33%', left: '50%', width: '50%', height: '34%', label: 'Côté droit' },
        bottom_left:  { top: '67%', left: '0%',  width: '50%', height: '33%', label: 'Arrière gauche' },
        bottom_right: { top: '67%', left: '50%', width: '50%', height: '33%', label: 'Arrière droit' },
    },
    quad: {
        top_left:     { top: '0%',  left: '0%',  width: '50%', height: '33%', label: 'Avant gauche' },
        top_right:    { top: '0%',  left: '50%', width: '50%', height: '33%', label: 'Avant droit' },
        left:         { top: '33%', left: '0%',  width: '50%', height: '34%', label: 'Côté gauche' },
        right:        { top: '33%', left: '50%', width: '50%', height: '34%', label: 'Côté droit' },
        bottom_left:  { top: '67%', left: '0%',  width: '50%', height: '33%', label: 'Arrière gauche' },
        bottom_right: { top: '67%', left: '50%', width: '50%', height: '33%', label: 'Arrière droit' },
    },
    moto: {
        top:            { top: '0%',    left: '25%', width: '50%', height: '16%', label: 'Avant' },
        top_left:       { top: '16%',   left: '0%',  width: '50%', height: '22%', label: 'Avant gauche' },
        top_right:      { top: '16%',   left: '50%', width: '50%', height: '22%', label: 'Avant droit' },
        middle_left:    { top: '38%',   left: '0%',  width: '50%', height: '24%', label: 'Côté gauche' },
        middle_right:   { top: '38%',   left: '50%', width: '50%', height: '24%', label: 'Côté droit' },
        bottom_left:    { top: '62%',   left: '0%',  width: '50%', height: '22%', label: 'Arrière gauche' },
        bottom_right:   { top: '62%',   left: '50%', width: '50%', height: '22%', label: 'Arrière droit' },
        bottom:         { top: '84%',   left: '25%', width: '50%', height: '16%', label: 'Arrière' },
    },
};

const CONTAINER_SIZES: Record<ChocType, { width: number; height: number }> = {
    voiture: { width: 180, height: 400 },
    quad:    { width: 200, height: 410 },
    moto:    { width: 195, height: 375 },
};

export interface RadioChocProps {
    options: { value: string; label?: string }[];
    name: string;
    value?: string | null;
    setValue: (name: string, value: string) => void;
    type: string;
    legend?: string;
    image: string;
}

/** Sélection du point de choc sur le schéma du véhicule. */
const RadioChoc = ({ options, name, value, setValue, type, legend, image }: RadioChocProps) => {
    const zones = ZONES[type as ChocType] || ZONES.voiture;
    const size = CONTAINER_SIZES[type as ChocType] || CONTAINER_SIZES.voiture;
    const selectedLabel = value && zones[value] ? zones[value].label : null;

    return (
        <fieldset className="flex flex-col items-center gap-4">
            {legend && <legend className="sr-only">{legend}</legend>}

            <div className="relative mx-auto" style={{ width: size.width, height: size.height }}>
                <img
                    src={image}
                    alt={`Point de choc ${type}`}
                    className="w-full h-full object-contain pointer-events-none select-none"
                    draggable={false}
                />

                {options.map((opt) => {
                    const zone = zones[opt.value];
                    if (!zone) return null;
                    const isSelected = value === opt.value;
                    const radioId = `${name}-${opt.value}`;

                    return (
                        <label
                            key={opt.value}
                            htmlFor={radioId}
                            className={`
                                absolute cursor-pointer rounded-sm transition-all duration-200
 ${isSelected
                                    ? 'bg-orange-500/25 border-2 border-orange-500 z-10'
                                    : 'border border-orange-500/15 bg-orange-500/5 hover:bg-orange-500/10'
                                }
                            `}
                            style={{
                                top: zone.top,
                                left: zone.left,
                                width: zone.width,
                                height: zone.height,
                            }}
                        >
                            <input
                                type="radio"
                                id={radioId}
                                name={name}
                                value={opt.value}
                                className="sr-only"
                                checked={isSelected}
                                onChange={(e) => setValue(name, e.target.value)}
                            />
                        </label>
                    );
                })}
            </div>

            {selectedLabel && (
                <p className="flex items-center gap-1.5 text-sm font-medium text-orange-600">
                    <Check className="w-4 h-4" />
                    {selectedLabel}
                </p>
            )}
        </fieldset>
    );
};

export default RadioChoc;
